import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';

export type ChipTone =
  | 'info'
  | 'success'
  | 'warn'
  | 'danger'
  | 'neutral'
  | 'accent'
  | 'solid'
  | 'outline';

export type ChipSize = 'default' | 'lg';

interface ChipProps {
  tone?: ChipTone;
  size?: ChipSize;
  dot?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

export default function Chip({
  tone,
  size = 'default',
  dot = false,
  icon,
  children,
  style,
  textStyle,
}: ChipProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;

  const height = size === 'lg' ? 26 : 22;
  const hPad = size === 'lg' ? 12 : 9;
  const fSize = size === 'lg' ? fontSize.sm : fontSize.xs;

  type ToneColors = { bg: string; border: string; color: string; dotColor: string };

  const toneMap: Record<string, ToneColors> = {
    info:    { bg: colors.infoSoft,    border: colors.infoLine,    color: colors.info,    dotColor: colors.info },
    success: { bg: colors.successSoft, border: colors.successLine, color: colors.success, dotColor: colors.success },
    warn:    { bg: colors.warnSoft,    border: colors.warnLine,    color: colors.warn,    dotColor: colors.warn },
    danger:  { bg: colors.dangerSoft,  border: colors.dangerLine,  color: colors.danger,  dotColor: colors.danger },
    neutral: { bg: colors.neutralSoft, border: colors.neutralLine, color: colors.ink, dotColor: colors.ink3 },
    accent:  { bg: colors.accentSoft,  border: colors.accentLine,  color: colors.accentInk, dotColor: colors.accent },
    solid:   { bg: colors.ink,         border: colors.ink,         color: colors.paper,   dotColor: colors.paper },
    outline: { bg: colors.transparent, border: colors.line,        color: colors.ink2,    dotColor: colors.ink3 },
  };

  const tc: ToneColors = tone
    ? toneMap[tone]
    : { bg: colors.card, border: colors.line, color: colors.ink2, dotColor: colors.ink3 };

  const containerStyle: ViewStyle = {
    height,
    paddingHorizontal: hPad,
    borderRadius: radii.full,
    borderWidth: 1,
    borderColor: tc.border,
    backgroundColor: tc.bg,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    ...Platform.select({
      web: { whiteSpace: 'nowrap' } as ViewStyle,
      default: {},
    }),
  };

  const labelStyle: TextStyle = {
    fontSize: fSize,
    fontWeight: fontWeight.medium,
    color: tc.color,
    fontFamily: theme.font.sans,
    includeFontPadding: false,
  };

  return (
    <View style={[containerStyle, style]}>
      {dot && (
        <View style={{
          width: 6,
          height: 6,
          borderRadius: 3,
          backgroundColor: tc.dotColor,
        }} />
      )}
      {icon && (
        <View style={{ width: 10, height: 10, alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </View>
      )}
      {children && (
        <Text style={[labelStyle, textStyle]} numberOfLines={1}>
          {children}
        </Text>
      )}
    </View>
  );
}
